import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Mail, Lock, ArrowRight, Loader2, Gift, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { Turnstile } from '@marsidev/react-turnstile';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { SEOHead } from '@/components/SEOHead';
import { supabase } from '@/integrations/supabase/client';
import { PasswordStrengthIndicator } from '@/components/auth/PasswordStrengthIndicator';
import { z } from 'zod';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

const signupSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain an uppercase letter')
    .regex(/[a-z]/, 'Password must contain a lowercase letter')
    .regex(/\d/, 'Password must contain a number'),
  confirmPassword: z.string(),
  agreeToTerms: z.literal(true, {
    errorMap: () => ({ message: 'You must agree to the terms' }),
  }),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

const Auth = () => {
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref');
  const [isSignUp, setIsSignUp] = useState(!!referralCode);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [showVerificationReminder, setShowVerificationReminder] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user, signUp, signIn, resetPasswordForEmail } = useAuth();

  useEffect(() => {
    if (user) {
      // Store referral code to apply after profile is created
      if (referralCode) {
        localStorage.setItem('pending_referral', referralCode);
      }
      toast({
        title: 'Welcome to SoulSafe! 💜',
        description: 'Your healing journey begins now.',
      });
      navigate('/dashboard');
    }
  }, [user, navigate, referralCode, toast]);

  // Load remember me preference
  useEffect(() => {
    const savedEmail = localStorage.getItem('soulsafe_remembered_email');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleResendVerification = async () => {
    setResendingEmail(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
      });
      if (error) throw error;
      toast({
        title: 'Email Sent',
        description: 'Verification email has been resent. Please check your inbox.',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to resend verification email',
        variant: 'destructive',
      });
    } finally {
      setResendingEmail(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !z.string().email().safeParse(email).success) {
      toast({
        title: 'Invalid Email',
        description: 'Please enter a valid email address.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      const { error } = await resetPasswordForEmail(email);
      if (error) {
        throw error;
      }
      setResetEmailSent(true);
      toast({
        title: 'Reset Link Sent',
        description: 'Check your email for a password reset link.',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to send reset email',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationErrors({});
    
    // Validate based on mode
    if (isSignUp) {
      const result = signupSchema.safeParse({ email, password, confirmPassword, agreeToTerms });
      if (!result.success) {
        const errors: Record<string, string> = {};
        result.error.errors.forEach((err) => {
          if (err.path[0]) {
            errors[err.path[0].toString()] = err.message;
          }
        });
        setValidationErrors(errors);
        toast({
          title: 'Validation Error',
          description: result.error.errors[0].message,
          variant: 'destructive',
        });
        return;
      }
    } else {
      const result = loginSchema.safeParse({ email, password });
      if (!result.success) {
        toast({
          title: 'Validation Error',
          description: result.error.errors[0].message,
          variant: 'destructive',
        });
        return;
      }
    }

    // Check for CAPTCHA token (optional - only if configured)
    if (!captchaToken && import.meta.env.VITE_TURNSTILE_SITE_KEY) {
      toast({
        title: 'Security Check Required',
        description: 'Please complete the security verification.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      if (isSignUp) {
        const { error } = await signUp(email, password, { captchaToken: captchaToken || undefined });
        if (error) {
          if (error.message.includes('already registered')) {
            toast({
              title: 'Account Exists',
              description: 'This email is already registered. Please sign in instead.',
              variant: 'destructive',
            });
          } else {
            throw error;
          }
        } else {
          setShowVerificationReminder(true);
          toast({
            title: 'Account Created',
            description: 'Please check your email to verify your account.',
          });
        }
      } else {
        // Handle remember me
        if (rememberMe) {
          localStorage.setItem('soulsafe_remembered_email', email);
        } else {
          localStorage.removeItem('soulsafe_remembered_email');
        }

        const { error } = await signIn(email, password, { captchaToken: captchaToken || undefined });
        if (error) {
          if (error.message.includes('Invalid login credentials')) {
            toast({
              title: 'Invalid Credentials',
              description: 'Please check your email and password.',
              variant: 'destructive',
            });
          } else if (error.message.includes('Email not confirmed')) {
            setShowVerificationReminder(true);
            toast({
              title: 'Email Not Verified',
              description: 'Please verify your email before signing in.',
              variant: 'destructive',
            });
          } else {
            throw error;
          }
        } else {
          navigate('/dashboard');
        }
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'An unexpected error occurred',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const switchMode = () => {
    setIsSignUp(!isSignUp);
    setShowVerificationReminder(false);
    setValidationErrors({});
    setConfirmPassword('');
    setAgreeToTerms(false);
  };

  return (
    <>
      <SEOHead
        title={isForgotPassword ? "Reset Password" : isSignUp ? "Sign Up" : "Sign In"}
        description="Create your SoulSafe account or sign in to continue your healing journey with trauma-informed support and resources."
        url="https://soulsafe.app/auth"
      />
      <div className="min-h-screen bg-gradient-to-br from-purple-light/40 via-background to-purple-light/20 flex items-center justify-center px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-4">
              <Heart className="h-8 w-8 text-purple" fill="currentColor" />
              <span className="font-display text-2xl font-bold text-foreground">SoulSafe</span>
            </div>
            <h1 className="font-display text-3xl font-bold text-foreground mb-2">
              {isForgotPassword 
                ? 'Reset Password' 
                : isSignUp 
                  ? 'Begin Your Healing Journey' 
                  : 'Welcome Back'}
            </h1>
            <p className="text-muted-foreground">
              {isForgotPassword
                ? "Enter your email and we'll send you a reset link"
                : isSignUp
                  ? 'Create your account to access support and resources'
                  : 'Sign in to continue your recovery path'}
            </p>
            
            {/* Referral badge */}
            {referralCode && !isForgotPassword && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple/10 text-purple text-sm font-medium"
              >
                <Gift className="w-4 h-4" />
                You were invited! Sign up to claim your bonus.
              </motion.div>
            )}
          </div>

          {/* Reset Email Sent Banner */}
          {resetEmailSent && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-6"
            >
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-medium text-foreground">Check your email</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    We've sent a password reset link to <strong>{email}</strong>. Click the link to set a new password.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3 border-green-500/30 text-green-600 hover:bg-green-500/10"
                    onClick={() => {
                      setIsForgotPassword(false);
                      setResetEmailSent(false);
                    }}
                  >
                    Back to Sign In
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Email Verification Reminder Banner */}
          {showVerificationReminder && !isForgotPassword && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-purple/10 border border-purple/30 rounded-xl p-4 mb-6"
            >
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-purple mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-medium text-foreground">Check your email</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    We've sent a verification link to <strong>{email}</strong>. Click the link to verify your account.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3 border-purple/30 text-purple hover:bg-purple/10"
                    onClick={handleResendVerification}
                    disabled={resendingEmail}
                  >
                    {resendingEmail ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Mail className="h-4 w-4 mr-2" />
                    )}
                    Resend verification email
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {!resetEmailSent && (
            <div className="bg-card rounded-2xl shadow-lg border border-border p-8">
              {isForgotPassword ? (
                <form onSubmit={handleForgotPassword} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="your@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10 focus-visible:ring-purple focus-visible:border-purple/50"
                        required
                      />
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    variant="purple"
                    className="w-full" 
                    disabled={loading}
                  >
                    {loading ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <>
                        Send Reset Link
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </Button>

                  <div className="mt-4 text-center">
                    <button
                      type="button"
                      onClick={() => setIsForgotPassword(false)}
                      className="text-purple hover:text-purple-dark hover:underline text-sm transition-colors"
                    >
                      Back to Sign In
                    </button>
                  </div>
                </form>
              ) : (
                <>
                  <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Email Field */}
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                        <Input
                          id="email"
                          type="email"
                          placeholder="your@email.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="pl-10 focus-visible:ring-purple focus-visible:border-purple/50"
                          required
                        />
                      </div>
                      {validationErrors.email && (
                        <p className="text-xs text-destructive">{validationErrors.email}</p>
                      )}
                    </div>

                    {/* Password Field */}
                    <div className="space-y-2">
                      <Label htmlFor="password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                        <Input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="pl-10 pr-10 focus-visible:ring-purple focus-visible:border-purple/50"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-purple transition-colors"
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                      {validationErrors.password && (
                        <p className="text-xs text-destructive">{validationErrors.password}</p>
                      )}
                      
                      {/* Password Strength Indicator (Sign Up only) */}
                      {isSignUp && <PasswordStrengthIndicator password={password} />}
                      
                      {/* Forgot Password Link (Login only) */}
                      {!isSignUp && (
                        <button
                          type="button"
                          onClick={() => setIsForgotPassword(true)}
                          className="text-sm text-purple hover:text-purple-dark hover:underline transition-colors"
                        >
                          Forgot password?
                        </button>
                      )}
                    </div>

                    {/* Confirm Password Field (Sign Up only) */}
                    {isSignUp && (
                      <div className="space-y-2">
                        <Label htmlFor="confirmPassword">Confirm Password</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                          <Input
                            id="confirmPassword"
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder="••••••••"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="pl-10 pr-10 focus-visible:ring-purple focus-visible:border-purple/50"
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-purple transition-colors"
                            aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                          >
                            {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                          </button>
                        </div>
                        {validationErrors.confirmPassword && (
                          <p className="text-xs text-destructive">{validationErrors.confirmPassword}</p>
                        )}
                        {confirmPassword && password === confirmPassword && (
                          <p className="text-xs text-green-600 flex items-center gap-1">
                            <CheckCircle className="h-3.5 w-3.5" />
                            Passwords match
                          </p>
                        )}
                      </div>
                    )}

                    {/* Remember Me (Login only) */}
                    {!isSignUp && (
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="rememberMe"
                          checked={rememberMe}
                          onCheckedChange={(checked) => setRememberMe(checked === true)}
                        />
                        <label
                          htmlFor="rememberMe"
                          className="text-sm text-muted-foreground cursor-pointer"
                        >
                          Remember me
                        </label>
                      </div>
                    )}

                    {/* Terms Checkbox (Sign Up only) */}
                    {isSignUp && (
                      <div className="space-y-1">
                        <div className="flex items-start space-x-2">
                          <Checkbox
                            id="agreeToTerms"
                            checked={agreeToTerms}
                            onCheckedChange={(checked) => setAgreeToTerms(checked === true)}
                            className="mt-0.5"
                          />
                          <label
                            htmlFor="agreeToTerms"
                            className="text-sm text-muted-foreground cursor-pointer leading-tight"
                          >
                            I agree to the{' '}
                            <a href="/terms" target="_blank" className="text-purple hover:underline">
                              Terms of Service
                            </a>{' '}
                            and{' '}
                            <a href="/privacy" target="_blank" className="text-purple hover:underline">
                              Privacy Policy
                            </a>
                          </label>
                        </div>
                        {validationErrors.agreeToTerms && (
                          <p className="text-xs text-destructive ml-6">{validationErrors.agreeToTerms}</p>
                        )}
                      </div>
                    )}

                    {import.meta.env.VITE_TURNSTILE_SITE_KEY && (
                      <div className="flex justify-center">
                        <Turnstile
                          siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
                          onSuccess={(token) => setCaptchaToken(token)}
                          onError={() => setCaptchaToken(null)}
                          onExpire={() => setCaptchaToken(null)}
                        />
                      </div>
                    )}

                    <Button 
                      type="submit" 
                      variant="purple"
                      className="w-full" 
                      disabled={loading}
                    >
                      {loading ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <>
                          {isSignUp ? 'Create Account' : 'Sign In'}
                          <ArrowRight className="ml-2 h-5 w-5" />
                        </>
                      )}
                    </Button>
                  </form>

                  <div className="mt-6 text-center">
                    <button
                      type="button"
                      onClick={switchMode}
                      className="text-purple hover:text-purple-dark hover:underline text-sm transition-colors"
                    >
                      {isSignUp
                        ? 'Already have an account? Sign in'
                        : "Don't have an account? Sign up"}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          <p className="text-center text-xs text-muted-foreground mt-6">
            SoulSafe provides educational and spiritual support only.
          </p>
        </motion.div>
      </div>
    </>
  );
};

export default Auth;